import React from "react";
import { motion } from "framer-motion";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  ScanSearch,
  ShieldAlert,
  FileCheck2,
  Workflow,
  Boxes,
  Plug,
  Scale,
  CheckCircle2,
} from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { findProduct, useContentLibrary } from "@/data/content";

const DEFAULT_PRODUCT_SLUG = "ai-inventory-compliance-governance";
const capabilityIcons = [ScanSearch, Boxes, ShieldAlert, Workflow, FileCheck2, Plug];

const LoadingProduct = () => (
  <div className="bg-white text-zinc-950 dark:bg-zinc-950 dark:text-zinc-100">
    <Nav />
    <main className="pt-32 md:pt-40 pb-24">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12 text-zinc-600 dark:text-zinc-400">Loading products...</div>
    </main>
    <Footer />
  </div>
);

export default function ProductPage() {
  const { slug } = useParams();
  const { data, loading } = useContentLibrary();

  if (loading) return <LoadingProduct />;
  if (!slug) return <ProductIndex products={data.products} />;

  const p = findProduct(data, slug);
  const path = `/products/${p?.slug || DEFAULT_PRODUCT_SLUG}`;

  if (!p) return <Navigate to="/products" replace />;

  return (
    <div className="bg-white text-zinc-950 dark:bg-zinc-950 dark:text-zinc-100">
      <SEO title={p.title} description={p.description} path={path} />
      <Nav />

      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-12 md:pb-14 overflow-hidden">
        <div className="absolute inset-0 grid-backdrop pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-8">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#002FA7] dark:text-[#7C9BFF]">
              {p.eyebrow}
            </span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="mt-5 font-display font-black tracking-tighter leading-[0.95] text-zinc-950 dark:text-zinc-50 text-5xl sm:text-6xl lg:text-7xl"
            >
              {p.titleLines.map((line, index) => (
                <React.Fragment key={line}>
                  {index > 0 && <br />}
                  {Number(p.highlightLine) === index ? (
                    <span className="text-[#002FA7] dark:text-[#7C9BFF]">{line}</span>
                  ) : (
                    line
                  )}
                </React.Fragment>
              ))}
            </motion.h1>
            <p className="mt-7 max-w-2xl text-lg text-zinc-600 dark:text-zinc-400 leading-relaxed">{p.description}</p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link
                to="/#contact"
                data-testid="product-cta-demo"
                className="group inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#00227A] text-white px-6 py-3.5 rounded-sm text-sm font-semibold transition-colors"
              >
                {p.primaryCta || "Book a demo"}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
              <a
                href="#how-it-works"
                data-testid="product-cta-learn"
                className="inline-flex items-center gap-2 border border-zinc-950 text-zinc-950 hover:bg-zinc-950 hover:text-white dark:border-zinc-200 dark:text-zinc-50 dark:hover:bg-zinc-50 dark:hover:text-zinc-950 px-6 py-3.5 rounded-sm text-sm font-semibold transition-colors"
              >
                {p.secondaryCta || "See how it works"}
              </a>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-4">
            <div className="grid grid-cols-2 gap-4">
              {p.heroStats.map((s) => (
                <div key={s.v} className="border border-zinc-200 dark:border-zinc-800 p-5 rounded-sm">
                  <div className="font-display font-black tracking-tighter text-3xl text-zinc-950 dark:text-zinc-50">{s.k}</div>
                  <div className="mt-1 text-[11px] font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-400">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-12 md:py-16 bg-zinc-50 border-y border-zinc-200 dark:bg-zinc-900 dark:border-zinc-800">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#002FA7] dark:text-[#7C9BFF]">Capabilities</span>
            <h2 className="mt-4 font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[1.02] text-zinc-950 dark:text-zinc-50">
              {p.capabilitiesHeading}
            </h2>
          </div>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {p.capabilities.map((c, i) => {
              const Icon = capabilityIcons[i % capabilityIcons.length];
              return (
                <motion.div
                  key={c.title}
                  data-testid={`capability-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.04 }}
                  className="bg-white border border-zinc-200 p-7 rounded-sm hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(9,9,11,0.18)] hover:border-zinc-950 dark:bg-zinc-950 dark:border-zinc-800 dark:hover:border-zinc-50 transition-all duration-300"
                >
                  <Icon size={22} strokeWidth={1.5} className="text-[#002FA7] dark:text-[#7C9BFF]" />
                  <h3 className="mt-5 font-display font-bold tracking-tight text-xl text-zinc-950 dark:text-zinc-50">{c.title}</h3>
                  <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">{c.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-12 md:py-16 bg-white dark:bg-zinc-950">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#002FA7] dark:text-[#7C9BFF]">How it works</span>
            <h2 className="mt-4 font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[1.02] text-zinc-950 dark:text-zinc-50">
              {p.flowHeading}
            </h2>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-0 border border-zinc-200 dark:border-zinc-800 rounded-sm overflow-hidden">
            {p.flow.map((s, i) => (
              <div
                key={s.title}
                data-testid={`flow-step-${i}`}
                className={`p-8 ${i < p.flow.length - 1 ? "md:border-r border-zinc-200 border-b md:border-b-0 dark:border-zinc-800" : ""}`}
              >
                <span className="font-mono text-[11px] tracking-widest text-zinc-400 dark:text-zinc-500">/ {String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-6 font-display font-bold tracking-tight text-2xl text-zinc-950 dark:text-zinc-50">{s.title}</h3>
                <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {p.sections.map((section, index) => (
        <ProductInfoSection key={section.title} section={section} dark={index % 2 === 0} />
      ))}

      {p.faqs.length > 0 && <ProductFaqSection faqs={p.faqs} />}

      <Footer />
    </div>
  );
}

const ProductIndex = ({ products }) => (
  <div className="bg-white text-zinc-950 dark:bg-zinc-950 dark:text-zinc-100">
    <SEO
      title="Products"
      description="Explore Jaisphere products for AI inventory, compliance governance, and enterprise technology operations."
      path="/products"
    />
    <Nav />
    <main className="pt-32 md:pt-40 pb-24">
      <section className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#002FA7] dark:text-[#7C9BFF]">Products</span>
        <h1 className="mt-5 font-display font-black tracking-tighter leading-[0.95] text-zinc-950 dark:text-zinc-50 text-5xl sm:text-6xl lg:text-7xl">
          Product portfolio.
        </h1>
        <p className="mt-7 max-w-2xl text-lg text-zinc-600 dark:text-zinc-400 leading-relaxed">
          Explore Jaisphere products for AI governance, compliance operations, and enterprise technology delivery.
        </p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {products.map((product) => (
            <Link
              key={`${product.sourcePath}-${product.slug}`}
              to={`/products/${product.slug}`}
              className="group border border-zinc-200 rounded-sm p-7 hover:border-zinc-950 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(9,9,11,0.18)] transition-all dark:border-zinc-800 dark:hover:border-zinc-50"
            >
              <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-400">
                {product.eyebrow || "Product"}
              </div>
              <h2 className="mt-4 font-display font-bold tracking-tight text-2xl text-zinc-950 group-hover:text-[#002FA7] dark:text-zinc-50 dark:group-hover:text-[#7C9BFF]">
                {product.title}
              </h2>
              <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">{product.description}</p>
              <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#002FA7] dark:text-[#7C9BFF]">
                View product <ArrowRight size={15} />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </main>
    <Footer />
  </div>
);

const ProductInfoSection = ({ section, dark }) => {
  const sectionClass = dark ? "bg-zinc-50 dark:bg-zinc-900" : "bg-white dark:bg-zinc-950";
  const eyebrowClass = "text-[#002FA7] dark:text-[#7C9BFF]";
  const headingClass = "text-zinc-950 dark:text-zinc-50";
  const bodyClass = "text-zinc-600 dark:text-zinc-400";
  const cardBorder = "border-zinc-200 dark:border-zinc-800";

  return (
    <section className={`py-12 md:py-16 ${sectionClass}`}>
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-12 gap-8 items-start mb-8">
          <div className="col-span-12 lg:col-span-7">
            <span className={`text-xs font-bold uppercase tracking-[0.22em] ${eyebrowClass}`}>{section.title}</span>
            <h2 className={`mt-4 font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[1.02] ${headingClass}`}>
              {section.title}
            </h2>
          </div>
          {section.intro && <p className={`col-span-12 lg:col-span-5 ${bodyClass}`}>{section.intro}</p>}
        </div>

        {section.items.length > 0 && (
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border-t ${cardBorder}`}>
            {section.items.map((item, i) => (
              <div
                key={item.title}
                className={`p-6 border-b ${cardBorder} ${i % 3 !== 2 ? "lg:border-r" : ""} ${i % 2 !== 1 ? "sm:border-r lg:border-r" : ""}`}
              >
                <div className="flex items-center gap-2">
                  <Scale size={16} className={eyebrowClass} />
                  <span className="font-display font-bold tracking-tight text-lg">{item.title}</span>
                </div>
                {item.desc && <p className={`mt-2 text-sm ${bodyClass}`}>{item.desc}</p>}
              </div>
            ))}
          </div>
        )}

        {(section.bullets.length > 0 || section.code) && (
          <div className="grid grid-cols-12 gap-10 items-start">
            {section.bullets.length > 0 && (
              <ul className="col-span-12 lg:col-span-6 space-y-4">
                {section.bullets.map((bullet) => (
                  <li key={bullet} className="flex items-start gap-3">
                    <span className={`mt-0.5 inline-flex items-center justify-center h-7 w-7 border rounded-sm ${cardBorder}`}>
                      <CheckCircle2 size={14} className={eyebrowClass} />
                    </span>
                    <span className="text-zinc-700 dark:text-zinc-300">{bullet}</span>
                  </li>
                ))}
              </ul>
            )}

            {section.code && (
              <div className="col-span-12 lg:col-span-6">
                <div className="border rounded-sm p-8 border-zinc-200 bg-white dark:bg-zinc-900 dark:border-zinc-800">
                  <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 dark:text-zinc-400">
                    Sample policy
                  </div>
                  <pre className="mt-4 text-xs leading-relaxed overflow-x-auto font-mono whitespace-pre-wrap text-zinc-800 dark:text-zinc-200">
                    {section.code.text}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

const ProductFaqSection = ({ faqs }) => (
  <section className="py-12 md:py-16 bg-zinc-50 border-t border-zinc-200 dark:bg-zinc-900 dark:border-zinc-800">
    <div className="mx-auto max-w-4xl px-6 md:px-10">
      <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#002FA7] dark:text-[#7C9BFF]">FAQ</span>
      <h2 className="mt-4 font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[1.02] text-zinc-950 dark:text-zinc-50">
        Questions, answered.
      </h2>
      <div className="mt-8 divide-y divide-zinc-200 border-y border-zinc-200 dark:divide-zinc-800 dark:border-zinc-800">
        {faqs.map((f) => (
          <details key={f.title} className="group py-5">
            <summary className="cursor-pointer flex items-center justify-between gap-4 font-display font-semibold text-lg tracking-tight text-zinc-950 dark:text-zinc-100">
              {f.title}
              <span className="text-zinc-400 dark:text-zinc-500 group-open:rotate-45 transition-transform">+</span>
            </summary>
            <p className="mt-3 text-zinc-600 dark:text-zinc-400 leading-relaxed">{f.desc}</p>
          </details>
        ))}
      </div>

      <div className="mt-8 bg-white border border-zinc-200 p-8 rounded-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 dark:bg-zinc-950 dark:border-zinc-800">
        <div>
          <div className="font-display font-bold tracking-tight text-xl text-zinc-950 dark:text-zinc-50">Ready to see Jaisphere in your stack?</div>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">A 30-minute working session, no slideware.</p>
        </div>
        <Link
          to="/#contact"
          className="bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-3 rounded-sm text-sm font-semibold transition-colors inline-flex items-center gap-2"
        >
          Book a demo <ArrowRight size={15} />
        </Link>
      </div>
    </div>
  </section>
);